import { useState, useEffect } from 'react';
import api from '../services/api';
import Swal from 'sweetalert2';

export default function Waiters() {
  const [waiters, setWaiters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadWaiters();
  }, []);

  const loadWaiters = async () => {
    try {
      const res = await api.get('/meseros');
      setWaiters(res.data);
    } catch {
      Swal.fire('Error', 'Could not load waiters', 'error');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setName('');
    setShowModal(true);
  };

  const openEdit = (waiter) => {
    setEditing(waiter);
    setName(waiter.nombre);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setName('');
    setEditing(null);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      Swal.fire('Error', 'Name is required', 'error');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/meseros/${editing.id}`, { nombre: name.trim(), is_active: editing.is_active });
        Swal.fire('Done!', 'Waiter updated successfully', 'success');
      } else {
        await api.post('/meseros', { nombre: name.trim() });
        Swal.fire('Done!', 'Waiter added successfully', 'success');
      }
      closeModal();
      loadWaiters();
    } catch {
      Swal.fire('Error', 'Could not save the waiter', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (waiter) => {
    const action = waiter.is_active ? 'deactivate' : 'activate';
    const result = await Swal.fire({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} ${waiter.nombre}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: `Yes, ${action}`,
      cancelButtonText: 'Cancel',
      confirmButtonColor: waiter.is_active ? '#dc3545' : '#28a745',
    });
    if (!result.isConfirmed) return;
    try {
      await api.put(`/meseros/${waiter.id}`, { nombre: waiter.nombre, is_active: !waiter.is_active });
      loadWaiters();
    } catch {
      Swal.fire('Error', 'Could not update the waiter', 'error');
    }
  };

  const handleDelete = async (waiter) => {
    const result = await Swal.fire({
      title: `Delete ${waiter.nombre}?`,
      text: 'This action cannot be undone',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc3545',
    });
    if (!result.isConfirmed) return;
    try {
      await api.delete(`/meseros/${waiter.id}`);
      Swal.fire('Done!', 'Waiter deleted', 'success');
      loadWaiters();
    } catch {
      Swal.fire('Error', 'Could not delete the waiter', 'error');
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  const active = waiters.filter((m) => m.is_active);
  const inactive = waiters.filter((m) => !m.is_active);

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-0">Waiters</h2>
          <small className="text-muted">
            {active.length} active
          </small>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>
          + Add Waiter
        </button>
      </div>

      {active.length === 0 && inactive.length === 0 ? (
        <div className="text-center text-muted mt-5">
          <h5>No waiters registered</h5>
          <p>Add the first waiter to get started</p>
          <button className="btn btn-primary" onClick={openCreate}>
            + Add Waiter
          </button>
        </div>
      ) : (
        <>
          <div className="row g-3 mb-4">
            {active.map((m) => (
              <div key={m.id} className="col-12 col-md-6 col-lg-4">
                <div className="card h-100" style={{ borderLeft: '4px solid #28a745', borderRadius: '10px' }}>
                  <div className="card-body d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        style={{
                          width: 42,
                          height: 42,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: 16,
                          flexShrink: 0,
                        }}
                      >
                        {m.nombre.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="fw-semibold">{m.nombre}</div>
                        <span className="badge bg-success" style={{ fontSize: '11px' }}>
                          Active
                        </span>
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <button className="btn btn-sm btn-outline-secondary" title="Edit" onClick={() => openEdit(m)}>
                        ✏️
                      </button>
                      <button className="btn btn-sm btn-outline-warning" title="Deactivate" onClick={() => toggleActive(m)}>
                        🔕
                      </button>
                      <button className="btn btn-sm btn-outline-danger" title="Delete" onClick={() => handleDelete(m)}>
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {inactive.length > 0 && (
            <>
              <p className="text-muted mb-2" style={{ fontSize: '13px' }}>
                <strong>Inactive ({inactive.length})</strong>
              </p>
              <div className="row g-3">
                {inactive.map((m) => (
                  <div key={m.id} className="col-12 col-md-6 col-lg-4">
                    <div className="card h-100" style={{ borderLeft: '4px solid #6c757d', borderRadius: '10px', opacity: 0.65 }}>
                      <div className="card-body d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            style={{
                              width: 42,
                              height: 42,
                              borderRadius: '50%',
                              background: '#6c757d',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontWeight: 700,
                              fontSize: 16,
                              flexShrink: 0,
                            }}
                          >
                            {m.nombre.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="fw-semibold text-muted">{m.nombre}</div>
                            <span className="badge bg-secondary" style={{ fontSize: '11px' }}>
                              Inactive
                            </span>
                          </div>
                        </div>
                        <div className="d-flex gap-2">
                          <button className="btn btn-sm btn-outline-success" title="Activate" onClick={() => toggleActive(m)}>
                            ✅
                          </button>
                          <button className="btn btn-sm btn-outline-danger" title="Delete" onClick={() => handleDelete(m)}>
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {showModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editing ? 'Edit Waiter' : 'Add Waiter'}</h5>
                <button className="btn-close" onClick={closeModal} />
              </div>
              <div className="modal-body">
                <label className="form-label">Full Name *</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="E.g.: Carlos Martinez"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                  autoFocus
                />
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : editing ? 'Save Changes' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}